import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { X, Search, Check, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLatinKeyboardGroupProps } from '../../hooks/useLatinKeyboardGroupProps';
import { formatDateTime } from '../../utils/format';
import { normalizeReceiptNumInput, normalizeRefundCodeInput } from '../../utils/receiptInput';
import { getOrderPriceRatio } from '../../utils/orderPricing';
import { printRefundReceipt, storeInfoForSessionPrint } from '../pos/Receipt';

const RefundModal = ({ isOpen, onClose, mode, themeSidebar, themeBorder, themeText, themeTextSecondary }) => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useSelector((state) => state.auth);
  const { containerProps: receiptLatinKeyboardProps } = useLatinKeyboardGroupProps();
  const { containerProps: approverLatinKeyboardProps } = useLatinKeyboardGroupProps();
  const [step, setStep] = useState(1); // 1: search & select, 2: review & approve
  const [receiptNum, setReceiptNum] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState({}); // { order_item_id: { quantity, restock } }
  const [reason, setReason] = useState('');
  const [approverId, setApproverId] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [approveError, setApproveError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!receiptNum.trim()) {
      setSearchError(t('alerts.pleaseEnterReceiptNumber'));
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    try {
      const res = await apiService.getOrderByReceipt(receiptNum);
      setOrder(res.data);
      setSelectedItems({});
      setReason('');
    } catch (err) {
      let searchErrMsg;
      if (err.body?.code === 'ORDER_NOT_FOUND_OR_EXPIRED') {
        const pd = Number(err.body.periodDays);
        searchErrMsg =
          pd === 1
            ? t('alerts.orderNotFoundOrOlderThanOneDay')
            : Number.isFinite(pd) && pd > 1
              ? t('alerts.orderNotFoundOrOlderThanDays', { days: pd })
              : t('alerts.failedToFindOrder');
      } else if (err.body?.code === 'ORDER_ALREADY_REFUNDED') {
        searchErrMsg = t('alerts.orderAlreadyRefunded');
      } else {
        searchErrMsg = err.body?.error || err.message || t('alerts.failedToFindOrder');
      }
      setSearchError(searchErrMsg);
      setOrder(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (!order?.items) return;
    const all = {};
    order.items.forEach((item) => {
      all[item.id] = {
        quantity: parseFloat(item.quantity) - parseFloat(item.refunded_quantity || 0),
        restock: false,
      };
    });
    setSelectedItems(all);
  };

  const handleItemToggle = (itemId, originalQty, refundedQty) => {
    const remaining = parseFloat(originalQty) - parseFloat(refundedQty || 0);
    if (remaining <= 0) return;
    if (selectedItems[itemId]) {
      const newSelected = { ...selectedItems };
      delete newSelected[itemId];
      setSelectedItems(newSelected);
    } else {
      setSelectedItems({
        ...selectedItems,
        [itemId]: { quantity: 0, restock: false },
      });
    }
  };

  const isPieceUnit = (item) => {
    const u = String(item?.product_unit_type || 'piece').toLowerCase();
    return u !== 'kg' && u !== 'weight';
  };

  const handleQuantityChange = (itemId, value) => {
    const item = order.items.find((i) => i.id === itemId);
    if (!item) return;
    const max = parseFloat(item.quantity) - parseFloat(item.refunded_quantity || 0);
    let qty = Math.min(Math.max(0, parseFloat(value) || 0), max);
    if (isPieceUnit(item)) {
      qty = Math.floor(qty);
    }
    setSelectedItems({
      ...selectedItems,
      [itemId]: { ...selectedItems[itemId], quantity: qty },
    });
  };

  const handleRestockToggle = (itemId) => {
    setSelectedItems({
      ...selectedItems,
      [itemId]: { ...selectedItems[itemId], restock: !selectedItems[itemId]?.restock },
    });
  };

  const calculateRefundSummary = () => {
    if (!order || !selectedItems) return { total: 0, items: [] };
    const priceRatio = getOrderPriceRatio(order);
    const taxPct = parseFloat(order.tax_percentage) || 0;
    const taxMultiplier = 1 + (taxPct / 100);
    const summary = [];
    let total = 0;
    Object.entries(selectedItems).forEach(([itemId, { quantity, restock }]) => {
      const item = order.items.find((i) => i.id === itemId);
      if (item && quantity > 0) {
        const itemSubtotal = parseFloat(quantity) * parseFloat(item.unit_price);
        const amount = Math.round(itemSubtotal * priceRatio * taxMultiplier * 100) / 100;
        total += amount;
        summary.push({
          ...item,
          refunded_quantity: quantity,
          refunded_amount: amount,
          restock,
        });
      }
    });
    total = Math.round(total * 100) / 100;
    return { total, items: summary };
  };

  const handleReview = () => {
    const selectedEntries = Object.entries(selectedItems);
    if (selectedEntries.length === 0) {
      setSearchError(t('refunds.noItemsSelected'));
      return;
    }
    const hasZeroQty = selectedEntries.some(
      ([, { quantity }]) => !(parseFloat(quantity) > 0)
    );
    if (hasZeroQty) {
      setSearchError(t('refunds.refundQuantityRequired'));
      return;
    }
    const summary = calculateRefundSummary();
    if (summary.items.length === 0) {
      setSearchError(t('refunds.refundQuantityRequired'));
      return;
    }
    if (!reason.trim()) {
      setSearchError(t('refunds.reasonRequired'));
      return;
    }
    if (reason.trim().length > 150) {
      setSearchError(t('alerts.refundReasonMaxLength'));
      return;
    }
    setStep(2);
    setApproveError(null);
  };

  const handleConfirmSubmit = (e) => {
    e.preventDefault();
    handleConfirm();
  };

  const handleConfirm = async () => {
    const trimmed = normalizeRefundCodeInput(approverId).trim();
    if (!trimmed) {
      setApproveError(t('alerts.pleaseEnterApproverCode'));
      return;
    }
    const refundCodeRegex = /^[A-Za-z0-9]{16}$/;
    if (!refundCodeRegex.test(trimmed)) {
      setApproveError(t('alerts.invalidApproverFormat'));
      return;
    }
    setSubmitLoading(true);
    setApproveError(null);
    try {
      const summary = calculateRefundSummary();
      const items = summary.items.map((item) => ({
        order_item_id: item.id,
        refunded_quantity: item.refunded_quantity,
        refunded_amount: item.refunded_amount,
        restock: item.restock,
        product_id: item.product_id,
      }));
      const refundResponse = await apiService.createCashierRefund({
        order_id: order.id,
        amount: summary.total,
        reason: reason.trim(),
        items,
        approved_by: trimmed,
      });

      try {
        const storeRes = await apiService.getStore();
        const storeInfo = storeInfoForSessionPrint(storeRes?.data || storeRes || {});
        await printRefundReceipt(
          {
            receipt_num: order.receipt_num || refundResponse?.data?.receipt_num || '',
            cashier_name: user?.full_name || t('settings.cashier'),
            refunded_at: refundResponse?.data?.created_at || new Date().toISOString(),
            items: summary.items,
            total: summary.total,
          },
          storeInfo
        );
      } catch (printErr) {
        console.error('Failed to print refund receipt:', printErr);
      }

      onClose();
      setStep(1);
      setReceiptNum('');
      setOrder(null);
      setSelectedItems({});
      setReason('');
      setApproverId('');
    } catch (err) {
      const errMsg = err.body?.code === 'INACTIVE_APPROVER'
        ? t('alerts.inactiveApprover')
        : err.body?.code === 'INVALID_APPROVER'
          ? t('alerts.invalidApprover')
          : err.body?.code === 'ORDER_ALREADY_REFUNDED'
            ? t('alerts.orderAlreadyRefunded')
            : (err.body?.error || err.message || t('alerts.failedToProcessRefund'));
      setApproveError(errMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setStep(1);
    setReceiptNum('');
    setOrder(null);
    setSelectedItems({});
    setReason('');
    setApproverId('');
    setSearchError(null);
    setApproveError(null);
  };

  if (!isOpen) return null;

  const summary = calculateRefundSummary();
  const isLight = mode !== 'black' && mode !== 'dark';
  const inputClasses = isLight
    ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
    : 'bg-transparent border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500';
  const secondaryButtonClasses = isLight
    ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
    : 'border-gray-600 text-gray-300 hover:bg-gray-700';
  const closeButtonClasses = isLight
    ? 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    : 'border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white';

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0, 0, 0, 0.6)' }}>
      <div
        className="rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col"
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{ background: themeSidebar, border: `1px solid ${themeBorder}` }}
      >
        <div className="p-4 flex items-center justify-between shrink-0" style={{ background: themeSidebar, borderBottom: `1px solid ${themeBorder}` }}>
          <h3 className="text-lg font-semibold" style={{ color: themeText }}>
            {step === 1 ? t('refunds.processRefund') : t('refunds.confirmRefund')}
          </h3>
          <button
            onClick={handleCancel}
            className={`p-1.5 rounded border ${closeButtonClasses}`}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-auto">
          {step === 1 ? (
            <div className="space-y-4">
              <form onSubmit={handleSearch} className="flex gap-2" {...receiptLatinKeyboardProps}>
                <input
                  type="text"
                  name="receiptNum"
                  inputMode="text"
                  maxLength={50}
                  value={receiptNum}
                  onChange={(e) => setReceiptNum(normalizeReceiptNumInput(e.target.value))}
                  placeholder={t('reports.receiptNo')}
                  className={`flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 ${inputClasses}`}
                  dir="ltr"
                  lang="en"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={searchLoading}
                  className={`px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 flex items-center gap-2 `}
                >
                  <Search size={18} />
                  {searchLoading ? t('common.loading') : t('common.search')}
                </button>
              </form>

              {searchError && (
                <div className={`p-3 rounded text-sm border flex items-center gap-2 ${isLight ? 'bg-red-50 text-red-700 border-red-200' : 'bg-red-900/40 text-red-200 border-red-700'}`}>
                  <AlertCircle size={18} />
                  {searchError}
                </div>
              )}

              {order && (
                <>
                  <div className={`p-3 rounded-lg ${mode === 'black' || mode === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`} style={{ border: `1px solid ${themeBorder}` }}>
                    <p className="text-sm" style={{ color: themeTextSecondary }}>{t('reports.orderNo')}: {order.receipt_num}</p>
                    <p className="text-sm" style={{ color: themeTextSecondary }}>{t('common.date')}: {formatDateTime(order.created_at)}</p>
                    <p className="text-sm" style={{ color: themeTextSecondary }}>{t('common.total')}: {t('currency.egp')} {Number(order.total_amount).toFixed(2)}</p>
                    {(order.promo_percentage || 0) > 0 && (
                      <p className="text-sm text-amber-500">
                        {t('pos.promo')}: {order.promo_name ? `${order.promo_name} (${order.promo_percentage}%)` : `${order.promo_percentage}%`}
                      </p>
                    )}
                    {(order.discount_percentage || 0) > 0 && (
                      <p className={`text-sm text-amber-500`}>{t('refunds.discountedOrder')} ({order.discount_percentage}%)</p>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <h4 className="font-medium" style={{ color: themeText }}>{t('refunds.selectItems')}:</h4>
                    <button
                      onClick={handleSelectAll}
                      className={`px-3 py-1 text-sm rounded border ${secondaryButtonClasses}`}
                    >
                      {t('refunds.fullRefund')}
                    </button>
                  </div>

                  <div className="space-y-2 max-h-64 overflow-auto">
                    {order.items?.map((item) => {
                      const refundedQty = parseFloat(item.refunded_quantity || 0);
                      const remaining = parseFloat(item.quantity) - refundedQty;
                      const isSelected = !!selectedItems[item.id];
                      const selectedQty = selectedItems[item.id]?.quantity || 0;
                      return (
                        <div
                          key={item.id}
                          className={`p-3 rounded-lg border ${
                            isSelected
                              ? isLight
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-blue-500 bg-blue-900/20'
                              : isLight
                                ? 'border-gray-300'
                                : 'border-gray-700'
                          }`}
                        >
                          <div className={`flex items-start gap-3 `}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleItemToggle(item.id, item.quantity, refundedQty)}
                              disabled={remaining <= 0}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="font-medium" style={{ color: themeText }}>{item.product_name}</p>
                              <p className="text-sm" style={{ color: themeTextSecondary }}>
                                {t('common.quantity')}: {item.quantity} | {t('common.price')}: {t('currency.egp')} {Number(item.unit_price).toFixed(2)} | {t('common.total')}: {t('currency.egp')}{' '}
                                {Number(item.total_price).toFixed(2)}
                              </p>
                              {refundedQty > 0 && (
                                <p className={`text-xs ${themeTextSecondary} text-amber-400`}>
                                  {t('reports.refunded')}: {refundedQty}
                                </p>
                              )}
                              {isSelected && (
                                <div className="mt-2 space-y-2">
                                  <div className={`flex items-center gap-2 `}>
                                    <label className="text-sm" style={{ color: themeTextSecondary }}>{t('refunds.refundQuantity')}:</label>
                                    <input
                                      type="number"
                                      step={isPieceUnit(item) ? '1' : '0.001'}
                                      min="0"
                                      max={remaining}
                                      value={selectedQty}
                                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      onKeyDown={(e) => {
                                        if (isPieceUnit(item) && (e.key === '.' || e.key === ',')) {
                                          e.preventDefault();
                                        }
                                      }}
                                      className={`w-24 px-2 py-1 rounded border text-sm ${inputClasses}`}
                                    />
                                    <span className="text-xs" style={{ color: themeTextSecondary }}>/ {remaining}</span>
                                  </div>
                                  <label className={`flex items-center gap-2 cursor-pointer ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                                    <input
                                      type="checkbox"
                                      checked={selectedItems[item.id]?.restock || false}
                                      onChange={() => handleRestockToggle(item.id)}
                                      className="rounded"
                                    />
                                    <span className="text-sm" style={{ color: themeTextSecondary }}>{t('refunds.restockItem')}</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themeTextSecondary }}>{t('refunds.refundReason')}:</label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      maxLength={150}
                      rows={2}
                      className={`w-full px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 resize-none h-16 ${inputClasses}`}
                      placeholder={t('refunds.enterReason')}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <form id="refundConfirmForm" onSubmit={handleConfirmSubmit} className="space-y-4">
              <div className={`p-4 rounded-lg ${mode === 'black' || mode === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`} style={{ border: `1px solid ${themeBorder}` }}>
                <h4 className="font-semibold mb-3" style={{ color: themeText }}>{t('refunds.refundDetails')}</h4>
                <div className="space-y-2">
                  {summary.items.map((item) => (
                    <div key={item.id} className={`flex justify-between text-sm `}>
                      <span style={{ color: themeTextSecondary }}>
                        {item.product_name} × {item.refunded_quantity}
                        {item.restock && ` (${t('refunds.restock')})`}
                      </span>
                      <span style={{ color: themeText }}>{t('currency.egp')} {item.refunded_amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="pt-2 flex justify-between font-semibold" style={{ borderTop: `1px solid ${themeBorder}` }}>
                    <span style={{ color: themeText }}>{t('refunds.refundAmount')}:</span>
                    <span style={{ color: themeText }}>{t('currency.egp')} {summary.total.toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${themeBorder}` }}>
                  <p className="text-sm" style={{ color: themeTextSecondary }}>
                    <strong>{t('refunds.refundReason')}:</strong> {reason}
                  </p>
                </div>
              </div>

              <div {...approverLatinKeyboardProps}>
                <label className="block text-sm font-medium mb-1" style={{ color: themeTextSecondary }}>
                  {t('refunds.approvedBy')} ({t('settings.manager')}/{t('settings.admin')} {t('refunds.refundCodeOrUuid')}):
                </label>
                <input
                  type="password"
                  name="approverRefundCode"
                  value={approverId}
                  onChange={(e) => setApproverId(normalizeRefundCodeInput(e.target.value))}
                  maxLength={16}
                  placeholder={t('refunds.refundCodeOrUuidPlaceholder')}
                  className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 ${inputClasses}`}
                  dir="ltr"
                  lang="en"
                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                />
              </div>

              {approveError && (
                <div className={`p-3 rounded text-sm border flex items-center gap-2 ${isLight ? 'bg-red-50 text-red-700 border-red-200' : 'bg-red-900/40 text-red-200 border-red-700'}`}>
                  <AlertCircle size={18} />
                  {approveError}
                </div>
              )}
            </form>
          )}
        </div>

        {((step === 1 && order) || step === 2) && (
          <div
            className={`p-4 flex gap-2 shrink-0 ${isRTL ? 'justify-start' : 'justify-end'}`}
            style={{ background: themeSidebar, borderTop: `1px solid ${themeBorder}` }}
          >
            {step === 1 ? (
              <>
                <button
                  onClick={handleCancel}
                  className={`px-4 py-2 rounded-lg border text-sm ${secondaryButtonClasses}`}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleReview}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                >
                  {t('refunds.confirmRefund')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setApproveError(null);
                  }}
                  className={`px-4 py-2 rounded-lg border text-sm ${secondaryButtonClasses}`}
                  disabled={submitLoading}
                >
                  {t('common.back')}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`px-4 py-2 rounded-lg border text-sm ${secondaryButtonClasses}`}
                  disabled={submitLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  form="refundConfirmForm"
                  disabled={submitLoading}
                  className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {submitLoading ? t('pos.processing') : t('refunds.confirmRefund')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RefundModal;
